import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { getDirectory } from "../../../mentions";
import { sendGiftApi, sendVirtualGiftApi } from "../../../giftsApi";
import { loadCredits, loadGiftCatalog } from "../../../creditsApi";
import { showToast } from "../../../toast";
import { GiftRetentionSelector, type DeliveryMode } from "./GiftRetentionSelector";
import { PrivateDropMediaSourcePicker } from "./PrivateDropMediaSourcePicker";
import { CameraCaptureView } from "./CameraCaptureView";
import { VideoRecorderView } from "./VideoRecorderView";
import { VoiceDropRecorder } from "./VoiceDropRecorder";
import { GiftMediaPreview, type PendingMedia } from "./GiftMediaPreview";
import { PrivateDropReview } from "./PrivateDropReview";
import { VirtualGiftCard } from "./VirtualGiftCard";
import type { GiftCatalogItem } from "../../../types";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE = 500 * 1024 * 1024;
const MIN_LOCK_LEAD_MINUTES = 5;
const MAX_MESSAGE_LENGTH = 300;

function minUnlockParts(): { date: string; time: string } {
  const d = new Date(Date.now() + MIN_LOCK_LEAD_MINUTES * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function unlockSummary(dateValue: string, timeValue: string): string {
  const d = new Date(`${dateValue}T${timeValue}`);
  if (Number.isNaN(d.getTime())) return "";
  const dateStr = d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  const timeStr = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${dateStr} at ${timeStr}`;
}

function categoryLabel(c: string): string {
  return c.charAt(0).toUpperCase() + c.slice(1);
}

type CustomStep = "source" | "camera" | "video" | "voice" | "compose" | "review";

// Dedicated "/gifts/send" page - replaces the small modal for the Gifts
// page's own primary "+ Send a Gift" entry point (PART 1). Lightbox.tsx's
// contextual "Send as Gift" action (gifting an exact gallery item, already
// mid-browse) deliberately keeps using the original SendGiftDialog.tsx
// modal unchanged - that flow is a quick, in-context shortcut with a
// preset file already chosen, not the general "browse the whole gift
// experience" flow this page is for.
//
// Two independent gift types share one page: Virtual (catalog item, costs
// credits) and Custom (the original feature - your own photo/video/voice,
// free). The Custom tab is a small state machine (CustomStep) walking
// through: pick a source -> capture/choose -> compose (preview + delivery
// mode + message) -> (Unlock later only) review -> send.
export function SendGiftScreen() {
  const navigate = useNavigate();
  const balance = useAppState((s) => s.credits);
  const catalog = useAppState((s) => s.giftCatalog);

  const [tab, setTab] = useState<"virtual" | "custom">("virtual");
  const recipients = getDirectory().filter((u) => u.email !== appState.currentUserEmail);
  const needsRecipientPicker = recipients.length > 1;
  const soleRecipient = recipients.length === 1 ? recipients[0] : null;

  const [recipientEmailChoice, setRecipientEmail] = useState("");
  const recipientEmail = recipientEmailChoice || soleRecipient?.email || "";
  const recipientName = recipients.find((r) => r.email === recipientEmail)?.fullName || recipientEmail;
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // --- Virtual tab state ---
  const [category, setCategory] = useState("all");
  const [selectedGiftId, setSelectedGiftId] = useState<string | null>(null);

  // --- Custom tab state ---
  const [customStep, setCustomStep] = useState<CustomStep>("source");
  const [media, setMedia] = useState<PendingMedia | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>("once");
  const [unlockDate, setUnlockDate] = useState("");
  const [unlockTime, setUnlockTime] = useState("");
  const [dateTimeError, setDateTimeError] = useState<string | null>(null);
  const [openTogether, setOpenTogether] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCredits();
    loadGiftCatalog();
  }, []);

  const categories = useMemo(() => ["all", ...Array.from(new Set(catalog.map((g) => g.category)))], [catalog]);
  const visibleCatalog = useMemo(() => (category === "all" ? catalog : catalog.filter((g) => g.category === category)), [catalog, category]);
  const selectedGift: GiftCatalogItem | null = catalog.find((g) => g.id === selectedGiftId) || null;

  function resetCustomTab(): void {
    setCustomStep("source");
    setMedia(null);
    setDeliveryMode("once");
    setUnlockDate("");
    setUnlockTime("");
    setDateTimeError(null);
    setOpenTogether(false);
    setMessage("");
    setError("");
  }

  function handleGalleryFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0] || null;
    if (!picked) return;
    const isVideo = /\.(mp4|mov|webm|avi)$/i.test(picked.name);
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (picked.size > maxSize) {
      setError(`File too large (max ${isVideo ? "500MB for videos" : "10MB for photos"}).`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setError("");
    setMedia({ kind: isVideo ? "video" : "image", file: picked });
    setCustomStep("compose");
  }

  async function handleSendCustom(): Promise<void> {
    if (!media) return;
    setError("");
    if (!recipientEmail) {
      setError("Choose who this gift is for.");
      return;
    }
    let unlockAtIso: string | null = null;
    if (deliveryMode === "unlock_later") {
      const parsedMs = Date.parse(`${unlockDate}T${unlockTime}`);
      if (!unlockDate || !unlockTime || !Number.isFinite(parsedMs) || parsedMs - Date.now() < MIN_LOCK_LEAD_MINUTES * 60 * 1000) {
        setDateTimeError("Choose a time in the future.");
        setCustomStep("compose");
        return;
      }
      unlockAtIso = new Date(parsedMs).toISOString();
    }

    const mode = deliveryMode === "once" ? "once" : "keep";
    setSending(true);
    const result = await sendGiftApi({
      recipientEmail,
      mode,
      file: media.file,
      unlockAt: unlockAtIso,
      message: message.trim() || undefined,
      openTogether: deliveryMode === "unlock_later" && openTogether,
    });
    setSending(false);
    if (!result.ok) {
      setError(result.error || "Failed to send gift. Try again.");
      setCustomStep(deliveryMode === "unlock_later" ? "review" : "compose");
      return;
    }
    showToast(deliveryMode === "unlock_later" ? "✓ Private Drop sent" : `Gift sent to ${result.gift.recipientName} ✓`, "success");
    resetCustomTab();
    navigate("/gifts");
  }

  function handleComposeContinue(): void {
    setError("");
    setDateTimeError(null);
    if (deliveryMode === "unlock_later") {
      const parsedMs = Date.parse(`${unlockDate}T${unlockTime}`);
      if (!unlockDate || !unlockTime || !Number.isFinite(parsedMs) || parsedMs - Date.now() < MIN_LOCK_LEAD_MINUTES * 60 * 1000) {
        setDateTimeError("Choose a time in the future.");
        return;
      }
      setCustomStep("review");
      return;
    }
    // View once / Keep send directly, matching today's behavior - Review
    // is specifically an Unlock-Later step (per the product brief).
    handleSendCustom();
  }

  async function handleSendVirtual(): Promise<void> {
    setError("");
    if (!recipientEmail) {
      setError("Choose who this gift is for.");
      return;
    }
    if (!selectedGift) {
      setError("Choose a gift.");
      return;
    }
    if (balance < selectedGift.price) {
      setError("Not enough credits for this gift.");
      return;
    }
    setSending(true);
    const result = await sendVirtualGiftApi({ giftId: selectedGift.id, recipientEmail, message: message.trim() || undefined });
    setSending(false);
    if (!result.ok) {
      setError(result.error || "Failed to send gift. Try again.");
      return;
    }
    showToast(`${selectedGift.emoji} Gift sent to ${result.gift.recipientName} ✓`, "success");
    navigate("/gifts");
  }

  const container = getVanillaEl<HTMLDivElement>("sendGiftView");
  if (!container) return null;

  const canSendVirtual = !!selectedGift && balance >= (selectedGift?.price ?? Infinity);

  return createPortal(
    <>
      <div className="send-gift-page-header">
        <button
          type="button"
          className="send-gift-page-back"
          aria-label="Back to Gifts"
          onClick={() => (tab === "custom" && customStep !== "source" ? setCustomStep("source") : navigate("/gifts"))}
        >
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
        <span className="send-gift-page-title">Send a Gift</span>
        <button
          type="button"
          className="send-gift-page-balance"
          aria-label={`${balance} credits - view Rewards`}
          onClick={() => navigate("/rewards")}
        >
          <i className="bi bi-gem" aria-hidden="true" /> {balance}
        </button>
      </div>

      <div className="send-gift-page-hero">
        <h1 className="send-gift-page-hero-title">Surprise them with something just for them.</h1>
      </div>

      <div className="send-gift-mode-tabs" role="group" aria-label="Gift type">
        <button
          type="button"
          className={tab === "virtual" ? "is-active" : ""}
          aria-pressed={tab === "virtual"}
          onClick={() => setTab("virtual")}
        >
          Virtual Gifts
        </button>
        <button
          type="button"
          className={tab === "custom" ? "is-active" : ""}
          aria-pressed={tab === "custom"}
          onClick={() => setTab("custom")}
        >
          Custom Gift
        </button>
      </div>

      {needsRecipientPicker && (
        <TextField
          select
          label="Send to"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          fullWidth
          size="small"
          disabled={sending}
          sx={{ mb: 2 }}
        >
          {recipients.map((u) => (
            <MenuItem key={u.email} value={u.email}>
              {u.fullName || u.email}
            </MenuItem>
          ))}
        </TextField>
      )}
      {!recipients.length && <p className="small muted mb-2">No one else to send gifts to yet.</p>}

      {tab === "virtual" ? (
        <>
          <div className="send-gift-section-heading">
            <span className="send-gift-section-label">Choose a gift</span>
          </div>
          <div className="send-gift-categories">
            {categories.map((c) => (
              <button key={c} type="button" className={category === c ? "is-active" : ""} onClick={() => setCategory(c)}>
                {c === "all" ? "All" : categoryLabel(c)}
              </button>
            ))}
          </div>
          <div className="virtual-gift-grid">
            {visibleCatalog.map((item) => (
              <VirtualGiftCard
                key={item.id}
                item={item}
                selected={selectedGiftId === item.id}
                balance={balance}
                onSelect={() => setSelectedGiftId((id) => (id === item.id ? null : item.id))}
              />
            ))}
          </div>

          {selectedGift && (
            <>
              <div className="send-gift-section-heading">
                <span className="send-gift-section-label">Review</span>
              </div>
              <div className="send-gift-review-card">
                <span className="send-gift-review-art" aria-hidden="true">
                  {selectedGift.emoji}
                </span>
                <span className="send-gift-review-body">
                  <span className="send-gift-review-name">{selectedGift.name}</span>
                  {recipientEmail && <span className="send-gift-review-recipient">To {recipientName}</span>}
                </span>
                <span className="send-gift-review-price">
                  <i className="bi bi-gem" aria-hidden="true" /> {selectedGift.price}
                </span>
              </div>
            </>
          )}

          {error && <div className="send-gift-error">{error}</div>}

          <button type="button" className="send-gift-submit" onClick={handleSendVirtual} disabled={sending || !canSendVirtual || !recipients.length}>
            <i className="bi bi-gift-fill" aria-hidden="true" />
            {sending ? "Sending…" : selectedGift ? `Send Gift · ${selectedGift.price} credits` : "Send Gift"}
          </button>
        </>
      ) : (
        <>
          <input ref={fileInputRef} type="file" accept="image/*,video/*" hidden onChange={handleGalleryFileChange} disabled={sending} />

          {customStep === "source" && (
            <PrivateDropMediaSourcePicker
              onTakePhoto={() => setCustomStep("camera")}
              onRecordVideo={() => setCustomStep("video")}
              onChooseFromGallery={() => fileInputRef.current?.click()}
              onVoiceDrop={() => setCustomStep("voice")}
            />
          )}

          {customStep === "camera" && (
            <CameraCaptureView
              onCapture={(file) => {
                setMedia({ kind: "image", file });
                setCustomStep("compose");
              }}
              onCancel={() => setCustomStep("source")}
              onFallbackToGallery={() => fileInputRef.current?.click()}
            />
          )}

          {customStep === "video" && (
            <VideoRecorderView
              onRecorded={(file) => {
                setMedia({ kind: "video", file });
                setCustomStep("compose");
              }}
              onCancel={() => setCustomStep("source")}
              onFallbackToGallery={() => fileInputRef.current?.click()}
            />
          )}

          {customStep === "voice" && (
            <VoiceDropRecorder
              onRecorded={(file, durationSec, waveform) => {
                setMedia({ kind: "voice", file, durationSec, waveform });
                setCustomStep("compose");
              }}
              onCancel={() => setCustomStep("source")}
            />
          )}

          {customStep === "compose" && media && (
            <>
              <GiftMediaPreview media={media} onChange={() => setCustomStep("source")} />

              <GiftRetentionSelector
                deliveryMode={deliveryMode}
                dateValue={unlockDate}
                timeValue={unlockTime}
                minDate={minUnlockParts().date}
                dateTimeError={dateTimeError}
                sending={sending}
                onSelectMode={(mode) => {
                  setDeliveryMode(mode);
                  setDateTimeError(null);
                  if (mode !== "unlock_later") setOpenTogether(false);
                  if (mode === "unlock_later" && !unlockDate) {
                    const min = minUnlockParts();
                    setUnlockDate(min.date);
                    setUnlockTime(min.time);
                  }
                }}
                onDateChange={setUnlockDate}
                onTimeChange={setUnlockTime}
              />

              <TextField
                label="Add a message (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
                size="small"
                disabled={sending}
                helperText={`${message.length}/${MAX_MESSAGE_LENGTH}`}
                sx={{ mt: 2 }}
              />

              {error && <div className="send-gift-error">{error}</div>}

              <button
                type="button"
                className="send-gift-submit"
                onClick={handleComposeContinue}
                disabled={sending || !recipients.length}
              >
                <i className="bi bi-gift-fill" aria-hidden="true" />
                {sending ? "Sending…" : deliveryMode === "unlock_later" ? "Review Drop" : "Send Gift"}
              </button>
            </>
          )}

          {customStep === "review" && media && (
            <PrivateDropReview
              media={media}
              recipientName={recipientName || "them"}
              unlockSummary={unlockSummary(unlockDate, unlockTime)}
              message={message.trim()}
              sending={sending}
              openTogether={openTogether}
              onToggleOpenTogether={setOpenTogether}
              onEdit={() => setCustomStep("compose")}
              onSend={handleSendCustom}
            />
          )}
        </>
      )}
    </>,
    container,
  );
}
